import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { querySuiteExecutions } from '@/features/executions/data';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { DetailTablePanel } from '@/features/shared/components/tableSystem/DetailTablePanel';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import {
  useCloneDateWindow,
  type CloneDateQuickFilter,
} from '@/features/shared/hooks/useCloneDateWindow';
import { SuiteDetailsPanels } from '@/features/suites/components/SuiteDetailsPanels';
import { SuiteExecutionsTable } from '@/features/suites/components/SuiteExecutionsTable';
import {
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/suites/pages/suiteExecutionsTableDefinition';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTests } from '@/features/tests/data/tests';
import {
  testsTableDefinition,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/tests/pages/testsTableDefinition';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box, type GridColumnVisibilityModel } from '@/ui/primitives';
import { Text } from '@/ui/primitives/Text';
import type { SuiteDetailPageViewModel } from '@/features/suites/hooks/useSuiteDetailPageViewModel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteDetailDto,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';

type SuiteDetailContentProps = {
  suite: SuiteDetailDto;
  viewModel: SuiteDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const SuiteDetailContent = ({
  suite,
  viewModel,
  onDelete,
  deleteLoading = false,
}: SuiteDetailContentProps): ReactElement => {
  const cloneExecutionDetailColumnOptions = useMemo(
    () => [
      { field: 'runNumber', label: 'Run', mandatory: true },
      { field: 'status', label: 'Status' },
      { field: 'tests', label: 'Tests' },
      { field: 'runId', label: 'Details' },
    ],
    [],
  );
  const cloneExecutionDetailDefaultColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      runNumber: true,
      status: true,
      tests: true,
      runId: true,
    }),
    [],
  );
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const { selectedOrganisationId } = useOrganisationFilter();
  useProjectFilter();
  const detailProjectId = suite.projectId;
  const { tagOptions } = useTagOptions({ projectId: detailProjectId });
  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneExecutionDetailDefaultColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    SuiteExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(testsTableDefinition.defaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(
    testsTableDefinition.defaultSortKey,
  );
  const [cloneSortDirection, setCloneSortDirection] = useState<CloneSortDirection>(
    testsTableDefinition.defaultSortDirection,
  );
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneExecutionMeta, setCloneExecutionMeta] =
    useState<TableQueryMeta<SuiteExecutionDto> | null>(null);
  const [cloneTestsMeta, setCloneTestsMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    null,
  );

  useEffect(() => {
    const nextQuickFilter: CloneDateQuickFilter =
      viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
        ? viewModel.range
        : '7d';
    setCloneDateQuickFilter(nextQuickFilter);
    setCloneFromDate(viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null);
    setCloneToDate(viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null);
  }, [viewModel.range, viewModel.selectedEndDay, viewModel.selectedStartDay]);

  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const cloneAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suite executions found for selected date range'
    : formatRangeWindowNoDataText('No suite executions found', cloneDateQuickFilter, false);
  const cloneTestsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneExecutionTableInstanceKey = useMemo(
    () =>
      [
        viewModel.suiteId,
        cloneDateQuickFilter,
        cloneDateWindow.startDay ?? 'none',
        cloneDateWindow.endDay ?? 'none',
        cloneExecutionStatusFilterKey,
        cloneExecutionTagFilterKey,
        debouncedCloneExecutionSearch,
      ].join(':'),
    [
      cloneDateQuickFilter,
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneExecutionStatusFilterKey,
      cloneExecutionTagFilterKey,
      debouncedCloneExecutionSearch,
      viewModel.suiteId,
    ],
  );
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneExecutionMeta, (item) => item.timestamp),
    [cloneExecutionMeta],
  );
  const cloneExecutionFixedFilters = useMemo(() => {
    const isLatestQuickFilter =
      cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
    return mergeFilters(
      buildEqFilterValue('suiteId', viewModel.suiteId),
      buildInFilter('status', cloneExecutionFilterStatuses),
      buildInFilter('tags', cloneExecutionFilterTags),
      isLatestQuickFilter
        ? buildEqFilterValue('isLatest', true)
        : buildDateRangeFilters(
            'timestamp',
            cloneDateWindow.startDay
              ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
              : null,
            cloneDateWindow.endDay
              ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
              : null,
          ),
    );
  }, [
    cloneDateQuickFilter,
    cloneDateWindow.endDay,
    cloneDateWindow.startDay,
    cloneExecutionFilterStatuses,
    cloneExecutionFilterTags,
    cloneFromDate,
    cloneToDate,
    viewModel.suiteId,
  ]);
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteExecutionDto>> => {
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return querySuiteExecutions(detailProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedCloneExecutionSearch,
        sorts: [sortOverride],
      });
    },
    [
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      detailProjectId,
      selectedOrganisationId,
    ],
  );
  const cloneTestsQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildEqFilterValue('suiteId', viewModel.suiteId),
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: testsTableDefinition.sortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(detailProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedCloneSearch,
        sorts: [sortOverride],
        filters: [...mappedFilters, ...toolbarFilters],
        metricsStartDay: cloneDateWindow.startDay ?? undefined,
        metricsEndDay: cloneDateWindow.endDay ?? undefined,
        previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          executionStatusCounts: true,
          metrics: true,
          metricDeltas: viewModel.showTableDeltas,
        },
      });
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      debouncedCloneSearch,
      detailProjectId,
      selectedOrganisationId,
      viewModel.showTableDeltas,
      viewModel.suiteId,
    ],
  );
  const suiteExecutionsNoDataText = formatRangeWindowNoDataText(
    'No suite executions found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Suite" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Suite"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting this suite also removes associated tests, suite executions, and test
                executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Suite"
        />
      }
      metadata={<SuiteDetailsPanels suite={suite} />}
      primaryContent={
        <Stack gap="none">
          <Box sx={{ mt: s('md') }}>
            <Stack gap="md">
              <Box
                sx={{
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: s('lg'),
                }}
              >
                <PageHeader
                  title="Suite Executions"
                  actions={
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      gap="md"
                      sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                    >
                      <TagsFilterField
                        value={viewModel.selectedTags}
                        options={viewModel.tagOptions}
                        onChange={viewModel.handleTagsChange}
                        emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                      />
                      <DashboardDateRangeControls
                        value={viewModel.range}
                        onChange={(nextRange) => {
                          viewModel.setRange(nextRange);
                          viewModel.setSelectedStartDay(null);
                          viewModel.setSelectedEndDay(null);
                        }}
                        displayStartDay={viewModel.pickerStartDay}
                        displayEndDay={viewModel.pickerEndDay}
                        isCustomRange={
                          Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                          !viewModel.isAllMode
                        }
                        onSelectedStartDayChange={viewModel.handleStartDayChange}
                        onSelectedEndDayChange={viewModel.handleEndDayChange}
                        fullWidthOnMobile
                      />
                    </Stack>
                  }
                />
              </Box>
              <Box sx={{ mt: s('md') }}>
                <Stack gap="lg">
                  <StatusMetricCardsRow cards={viewModel.suiteMetricCards} />
                  <MetricValueCardsRow items={viewModel.metricValueItems} />
                </Stack>
              </Box>
              <Box sx={{ mt: s('md') }}>
                <ExecutionMetricsChartPanel
                  chartTab={viewModel.chartTab}
                  tabsAriaLabel="Suite detail chart tabs"
                  onChartTabChange={viewModel.setChartTab}
                  showChartDateClear={viewModel.showChartDateClear}
                  onChartDateClear={viewModel.handleChartDateClear}
                  chartHeight={viewModel.chartHeight}
                  suppressChartAnimation={viewModel.suppressChartAnimation}
                  stackedChartData={viewModel.stackedChartData}
                  passRateChartData={viewModel.passRateChartData}
                  frequencyChartData={viewModel.frequencyChartData}
                  durationChartData={viewModel.durationChartData}
                  onChartSelect={viewModel.handleChartSelect}
                  showLineChartPoints={viewModel.showLineChartPoints}
                  emptyStateTitle={suiteExecutionsNoDataText}
                />
              </Box>
              <Box sx={{ mt: s('lg') }}>
                <DetailTablePanel
                  tabLabel="Executions"
                  tabsAriaLabel="Suite detail executions panel"
                  toolbarSlot={
                    <TableToolbar<CloneExecutionSortKey, SuiteExecutionStatus>
                      title={undefined}
                      searchAriaLabel="Search suite executions"
                      searchValue={debouncedCloneExecutionSearch}
                      onDebouncedSearchChange={setDebouncedCloneExecutionSearch}
                      columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        cloneExecutionDetailDefaultColumnVisibilityModel
                      }
                      columnOptions={cloneExecutionDetailColumnOptions}
                      onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                      sortKey={cloneExecutionSortKey}
                      sortDirection={cloneExecutionSortDirection}
                      defaultSortKey={cloneExecutionDefaultSortKey}
                      defaultSortDirection={cloneExecutionDefaultSortDirection}
                      sortOptions={cloneExecutionSortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setCloneExecutionSortKey(nextSortKey);
                        setCloneExecutionSortDirection(nextSortDirection);
                      }}
                      filterStatuses={cloneExecutionFilterStatuses}
                      statusOptions={cloneExecutionStatusOptions}
                      onFilterStatusesChange={setCloneExecutionFilterStatuses}
                      filterTags={cloneExecutionFilterTags}
                      onFilterTagsChange={setCloneExecutionFilterTags}
                      dateQuickFilter={cloneDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setCloneDateQuickFilter(next);
                        setCloneFromDate(null);
                        setCloneToDate(null);
                      }}
                      fromDate={cloneFromDate}
                      toDate={cloneToDate}
                      onFromDateChange={setCloneFromDate}
                      onToDateChange={setCloneToDate}
                      availableTags={cloneExecutionAvailableTags}
                      includeLatestQuickFilter
                      statusFilterLabel="Status"
                      summaryCount={cloneExecutionMeta?.total ?? null}
                      summarySingularLabel="suite execution"
                      summaryPluralLabel="suite executions"
                      previewFromDate={
                        cloneDateQuickFilter === 'latest' ? cloneExecutionDateBounds.from : null
                      }
                      previewToDate={
                        cloneDateQuickFilter === 'latest' ? cloneExecutionDateBounds.to : null
                      }
                    />
                  }
                >
                  <SuiteExecutionsTable
                    key={cloneExecutionTableInstanceKey}
                    persistKey={null}
                    hideNameColumn
                    runColumnFirst
                    queryKey={[
                      'suite-detail-executions-table',
                      viewModel.suiteId,
                      selectedOrganisationId ?? null,
                      detailProjectId,
                      cloneDateQuickFilter,
                      cloneExecutionSortKey,
                      cloneExecutionSortDirection,
                      cloneExecutionStatusFilterKey,
                      cloneExecutionTagFilterKey,
                      debouncedCloneExecutionSearch,
                      cloneDateWindow.startDay,
                      cloneDateWindow.endDay,
                    ]}
                    queryFn={cloneExecutionQueryFn}
                    fixedFilters={cloneExecutionFixedFilters}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialPageSize={5}
                    noRowsText={cloneExecutionsNoRowsText}
                    showToolbar={false}
                    disableColumnSorting
                    disableColumnMenu
                    search={debouncedCloneExecutionSearch}
                    onQueryMeta={setCloneExecutionMeta}
                    columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                    onColumnVisibilityModelChange={(nextModel) => {
                      setCloneExecutionColumnVisibilityModel({
                        ...nextModel,
                        runNumber: true,
                      });
                    }}
                  />
                </DetailTablePanel>
              </Box>
              <Box sx={{ mt: s('lg') }}>
                <DetailTablePanel
                  tabLabel="Tests"
                  tabsAriaLabel="Suite detail tests panel"
                  toolbarSlot={
                    <TableToolbar<CloneSortKey, TestExecutionStatus>
                      title={undefined}
                      searchAriaLabel={testsTableDefinition.searchAriaLabel}
                      searchValue={debouncedCloneSearch}
                      onDebouncedSearchChange={setDebouncedCloneSearch}
                      columnVisibilityModel={cloneColumnVisibilityModel}
                      defaultColumnVisibilityModel={
                        testsTableDefinition.defaultColumnVisibilityModel
                      }
                      columnOptions={testsTableDefinition.columnOptions}
                      onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                      sortKey={cloneSortKey}
                      sortDirection={cloneSortDirection}
                      defaultSortKey={testsTableDefinition.defaultSortKey}
                      defaultSortDirection={testsTableDefinition.defaultSortDirection}
                      sortOptions={testsTableDefinition.sortOptions}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setCloneSortKey(nextSortKey);
                        setCloneSortDirection(nextSortDirection);
                      }}
                      filterStatuses={cloneFilterStatuses}
                      statusOptions={testsTableDefinition.statusOptions}
                      onFilterStatusesChange={setCloneFilterStatuses}
                      filterTags={cloneFilterTags}
                      onFilterTagsChange={setCloneFilterTags}
                      dateQuickFilter={cloneDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setCloneDateQuickFilter(next);
                        setCloneFromDate(null);
                        setCloneToDate(null);
                      }}
                      fromDate={cloneFromDate}
                      toDate={cloneToDate}
                      onFromDateChange={setCloneFromDate}
                      onToDateChange={setCloneToDate}
                      availableTags={cloneAvailableTags}
                      summaryCount={cloneTestsMeta?.total ?? null}
                      summarySingularLabel="test"
                      summaryPluralLabel="tests"
                    />
                  }
                >
                  <TestsTable
                    persistKey={null}
                    queryKey={[
                      'suite-detail-tests-table',
                      viewModel.suiteId,
                      selectedOrganisationId ?? null,
                      detailProjectId,
                      cloneDateQuickFilter,
                      cloneSortKey,
                      cloneSortDirection,
                      cloneStatusFilterKey,
                      cloneTagFilterKey,
                      debouncedCloneSearch,
                      cloneDateWindow.startDay,
                      cloneDateWindow.endDay,
                    ]}
                    queryFn={cloneTestsQueryFn}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialPageSize={5}
                    showDeltas={viewModel.showTableDeltas}
                    sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
                    metricsStartDay={cloneDateWindow.startDay ?? undefined}
                    metricsEndDay={cloneDateWindow.endDay ?? undefined}
                    noRowsText={cloneTestsNoRowsText}
                    showToolbar={false}
                    disableColumnSorting
                    disableColumnMenu
                    showSuiteLinks={false}
                    includeSuiteInNameColumn={false}
                    search={debouncedCloneSearch}
                    onQueryMeta={setCloneTestsMeta}
                    columnVisibilityModel={cloneColumnVisibilityModel}
                    onColumnVisibilityModelChange={(nextModel) => {
                      setCloneColumnVisibilityModel({
                        ...nextModel,
                        name: true,
                      });
                    }}
                  />
                </DetailTablePanel>
              </Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
};
